import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Container } from '@/components/generals/container'
import { Reveal } from '@/components/generals/reveal'
import { SectionLabel } from '@/components/generals/section-label'

import { FOUNDER_STATS, TEAM } from '@/constants/content'
import { garage } from '@/constants/garage'

export const TeamSection: FC = (): ReactNode => {
    return (
        <section id='equipe' aria-labelledby='equipe-title' className='bg-surface py-20 lg:py-32'>
            <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10 xl:gap-16'>
                <figure className='lg:sticky lg:top-28 lg:col-span-5 lg:self-start'>
                    <div className='relative aspect-[4/5] overflow-hidden rounded-xl bg-border'>
                        <Image
                            src='/images/erwan-kermarec.jpg'
                            alt='Erwan Kermarec, fondateur du garage, bras croisés devant une voiture levée au pont'
                            fill
                            sizes='(min-width: 1440px) 540px, (min-width: 1024px) 38vw, 100vw'
                            className='object-cover object-[62%_30%]'
                        />
                    </div>

                    <figcaption className='label-mono mt-3 text-muted-foreground'>
                        Erwan Kermarec, dans l’atelier de l’île de Nantes
                    </figcaption>
                </figure>

                <div className='lg:col-span-7 lg:pt-4'>
                    <SectionLabel index='05'>Le patron et l’équipe</SectionLabel>

                    <h2 id='equipe-title' className='heading mt-5 text-title'>
                        Erwan Kermarec, 28&nbsp;ans de métier et une règle simple.
                    </h2>

                    <Reveal>
                        <blockquote className='mt-10 border-l-4 border-primary pl-6'>
                            <p className='font-heading text-[clamp(1.375rem,1.1rem+1vw,1.875rem)] font-semibold leading-[1.3] tracking-[-0.015em]'>
                                «&nbsp;Je ne touche à rien sans votre accord. Si je trouve autre chose, je vous appelle, photo à l’appui, et c’est vous qui décidez.&nbsp;»
                            </p>
                        </blockquote>
                    </Reveal>

                    <p className='mt-8 max-w-[38rem] text-muted-foreground'>
                        Formé en concession, chef d’atelier pendant dix ans, Erwan ouvre le garage en {garage.founded} avec une idée fixe&nbsp;: qu’on puisse confier sa voiture comme on confie ses clés à un voisin de confiance. Aujourd’hui, l’équipe compte cinq personnes, et la règle n’a pas bougé.
                    </p>

                    <dl className='mt-10 grid grid-cols-3 border-y border-border'>
                        {FOUNDER_STATS.map((stat, index) => (
                            <div key={stat.label} className={index === 0 ? 'py-5 pr-3' : 'border-l border-border py-5 pl-4 pr-3 sm:pl-6'}>
                                <dt className='heading text-[clamp(1.5rem,1.2rem+1.2vw,2.25rem)] leading-none tabular-nums'>
                                    {stat.figure}
                                </dt>

                                <dd className='mt-2 text-sm leading-snug text-muted-foreground'>
                                    {stat.label}
                                </dd>
                            </div>
                        ))}
                    </dl>

                    <h3 className='label-mono mt-14 text-muted-foreground'>
                        Ceux qui s’occupent de votre voiture
                    </h3>

                    <ul className='mt-4 grid border-t border-border sm:grid-cols-2'>
                        {TEAM.map((member, index) => (
                            <li
                                key={member.name}
                                className={index % 2 === 0 ? 'border-b border-border py-6 sm:pr-6' : 'border-b border-border py-6 sm:border-l sm:pl-6'}
                            >
                                <p className='text-lg font-semibold'>
                                    {member.name}
                                </p>

                                <p className='mt-0.5 text-sm font-medium text-signal-text'>
                                    {member.role}
                                </p>

                                <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                                    {member.detail}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </Container>
        </section>
    )
}
